/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { createPortal } from 'react-dom';
import { Box, 
  //Grid, Divider, 
  useMediaQuery, useTheme } from '@mui/material';
import {
  ListCollegeByGroup,
  type_of_school_convert,
  MY_COLLEGES_LIST_NAME,
  CollegeListGroup,
  PLAN_LIST_LIMITS,
  getMySchoolsMax,
  ModalAddList,
  getCollegeKey,
  isSameCollege,
} from './CollegeSearch';
import Modal from 'react-bootstrap/Modal';
import collegeServices from 'services/collegeServices';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useHistory } from 'react-router-dom';
import { routers } from 'router/router';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import { checkEnableParseJSON, reorder } from 'components/helper/helper';
import LoadingContent from 'components/LoadingContent';
import SavingIndicator from './SavingIndicator';
import { InlineSpinner, usePendingAction } from './usePendingAction';
import counselorServices from 'services/counselorServices';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { getAccessToken, getUserId, removeUserId, removeAccessToken } from 'utils/sessionStorage';
import { getCamBaseUrl, isGradMapSessionValid, notifyCamSessionExpired, openInCam, showCamPopupBlockedToast } from 'utils/camAuth';
import toast from 'react-hot-toast';
import { STRIPE_PRICE_NAME } from 'constants/common';
import {
  EMPTY_LIKELIHOOD_RANGES,
  formatLikelihoodRange,
  LIKELIHOOD_BALANCE_BUCKETS,
  MY_TAKE_TO_RANGE_KEY,
  normalizeLikelihoodRanges,
} from 'constants/likelihoodBalance';
import type { LikelihoodRanges } from 'constants/likelihoodBalance';
import type { AdmissionResultUi } from 'constants/admissionResult';
import {
  ACCEPTED_ADMISSION,
  admissionChipStyleFor,
  admissionLabelFor,
  ADMISSION_OPTIONS,
  DEFAULT_ADMISSION_RESULT,
  normalizeAdmissionResultUi,
  TRACKING_ADMISSION,
} from 'constants/admissionResult';
import 'styles/college-management.css';
import { LightDropdown } from './LightDropdown';

// ── Brand tokens ─────────────────────────────────────────────────────────────
const BRAND_NAVY   = '#2E5266';
const BRAND_ORANGE = '#F4A261';

// ── My Take ladder ───────────────────────────────────────────────────────────
type MyTake = 'likely' | 'target-likely' | 'target' | 'target-reach' | 'reach' | 'reach-far-reach' | 'far-reach';

const MY_TAKE_OPTIONS: { key: MyTake; label: string; bg: string; text: string; border: string }[] = [
  { key: 'likely',          label: 'Likely',          bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  { key: 'target-likely',   label: 'Target-Likely',   bg: '#ecfccb', text: '#3f6212', border: '#bef264' },
  { key: 'target',          label: 'Target',          bg: '#fef9c3', text: '#713f12', border: '#fde047' },
  { key: 'target-reach',    label: 'Target-Reach',    bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  { key: 'reach',           label: 'Reach',           bg: '#ffedd5', text: '#9a3412', border: '#fdba74' },
  { key: 'reach-far-reach', label: 'Reach-Far Reach', bg: '#ffe4e6', text: '#9f1239', border: '#fda4af' },
  { key: 'far-reach',       label: 'Far Reach',       bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
];

// ── My Take persistence helpers ───────────────────────────────────────────────
// "My Take" is stored on each college as `student_ranking` (a human-readable
// label) inside colleges_array, so the backend (UNIFY-COLLEGES-001) syncs it to
// CAM. `counselor_ranking` is a separate field owned by the counselor.
const normalizeTake = (value?: string | null): string =>
  (value ?? '').toLowerCase().replace(/[^a-z]/g, '');

// Maps every normalized variant (incl. counselor label spellings like
// "Likely-Target" / "Target- Reach") back to a canonical MyTake key.
const TAKE_KEY_BY_NORM: Record<string, MyTake> = {
  likely: 'likely',
  targetlikely: 'target-likely',
  likelytarget: 'target-likely',
  target: 'target',
  targetreach: 'target-reach',
  reachtarget: 'target-reach',
  reach: 'reach',
  reachfarreach: 'reach-far-reach',
  farreachreach: 'reach-far-reach',
  farreach: 'far-reach',
};

const takeKeyFromValue = (value?: string | null): MyTake | null =>
  TAKE_KEY_BY_NORM[normalizeTake(value)] ?? null;

const takeLabelFromKey = (key: MyTake): string =>
  MY_TAKE_OPTIONS.find((o) => o.key === key)?.label ?? key;

const COUNSELOR_TAKE_EMPTY = {
  label: 'Not yet reviewed',
  bg: '#f3f4f6',
  text: '#6B7280',
  border: '#e5e7eb',
} as const;

function sortCollegeKey(college: any): number {
  const schoolId = Number(college?.school_id);
  if (Number.isFinite(schoolId)) return schoolId;
  const collegeId = Number(college?.college_id ?? college?.id);
  if (Number.isFinite(collegeId)) return collegeId;
  return 0;
}

function computeAdmissionSummary(
  colleges: any[],
  admissionMap: Record<string, AdmissionResultUi>,
) {
  let tracking = 0;
  let submitted = 0;
  let accepted = 0;
  for (const c of colleges) {
    const key = getCollegeKey(c);
    const a = (key ? admissionMap[key] : undefined) ?? DEFAULT_ADMISSION_RESULT;
    if (TRACKING_ADMISSION.includes(a)) tracking += 1;
    if (a === 'submitted') submitted += 1;
    if (ACCEPTED_ADMISSION.includes(a)) accepted += 1;
  }
  return { tracking, submitted, accepted };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map((w) => w[0]).filter(Boolean).join('').toUpperCase();
}

function IconX({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function IconGraduationCap() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.25 }}>
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  );
}
function IconDragHandle() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
      <line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="18" x2="16" y2="18"/>
    </svg>
  );
}
function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}
function IconEye() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}
function IconPencil() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/>
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m5 0V4a2 2 0 012-2h2a2 2 0 012 2v2"/>
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

const FIELD_LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: '#6B7280',
  fontFamily: "'DM Sans', sans-serif",
  flexShrink: 0,
};

function FieldLabelGroup({ label, children, title }: {
  label: string;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <span
      title={title}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif" }}
    >
      <span style={FIELD_LABEL_STYLE}>{label}</span>
      {children}
    </span>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const MyCollege = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [myLikedCurrentList, setMyLikedCurrentList] = React.useState<any[]>([]);
  const dispatch = useAppDispatch();
  const history = useHistory();
  let studentId = getUserId();
  let token = getAccessToken();

  const [myCurrentList, setMyCurrentList] = React.useState<ListCollegeByGroup>([]);
  const [savedListsData, setSavedListsData] = React.useState<{
    studentLists: CollegeListGroup[];
    counselorLists: CollegeListGroup[];
  }>({ studentLists: [], counselorLists: [] });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'my-schools' | 'saved-lists'>('my-schools');

  const [admission, setAdmission] = React.useState<Record<string, AdmissionResultUi>>({});
  const [showNotApplying, setShowNotApplying] = React.useState(false);

  // SPEC_Undecided_Toggle (v1.1) — student-level "I'm undecided about my Areas
  // of Interest right now" flag. Fetched once on mount from student.preferences
  // and mirrored on CAM Organize Your Schools via the same endpoint.
  const [areasOfInterestUndecided, setAreasOfInterestUndecided] =
    React.useState<boolean>(false);
  const [areasUndecidedSaving, setAreasUndecidedSaving] =
    React.useState<boolean>(false);
  const [camOpenContext, setCamOpenContext] = React.useState<{
    requirementsReturnTo: string | null;
    likelihoodRanges: LikelihoodRanges;
  } | null>(null);
  const likelihoodRanges = camOpenContext?.likelihoodRanges ?? EMPTY_LIKELIHOOD_RANGES;

  // CAM-CATEGORIES-001: "My Schools" bulk category organization (mirrors CAM's
  // Organize Your Schools categories — Dream Schools / Uncategorized / custom).
  const [camCategories, setCamCategories] = React.useState<
    Array<{ id: string; name: string; color: string; isDefault: boolean }>
  >([]);
  const [schoolCategoryByLegacyId, setSchoolCategoryByLegacyId] = React.useState<
    Record<string, string | null>
  >({});
  const [schoolCategoryBySchoolId, setSchoolCategoryBySchoolId] = React.useState<
    Record<string, string | null>
  >({});
  const [selectedSchoolIds, setSelectedSchoolIds] = React.useState<Set<number>>(
    new Set(),
  );
  const [isMovingCategory, setIsMovingCategory] = React.useState(false);
  const [pendingCategoryId, setPendingCategoryId] = React.useState<string>('');
  const CATEGORY_COLOR_PALETTE = [
    '#0EA5E9', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#EC4899',
  ];

  // CAM-CATEGORIES-001: Primary/Alternate Major gate — CAM requires a Primary
  // Major before a school can be organized into a category.
  const [schoolMajorByLegacyId, setSchoolMajorByLegacyId] = React.useState<
    Record<string, { intendedMajor: string | null; alternateMajor: string | null }>
  >({});
  const [schoolMajorBySchoolId, setSchoolMajorBySchoolId] = React.useState<
    Record<string, { intendedMajor: string | null; alternateMajor: string | null }>
  >({});
  const [majorOptionsByKey, setMajorOptionsByKey] = React.useState<
    Record<string, Array<{ name: string }>>
  >({});

  const stripeSubscriptionInfo = useAppSelector(
    (store: any) => store.basicInformation.stripeSubscriptionInfo
  );
  const planName: string = stripeSubscriptionInfo?.subcription_plan ?? STRIPE_PRICE_NAME.GradMapStarter;
  const isStarter = planName === STRIPE_PRICE_NAME.GradMapStarter;
  const mySchoolsMax: number = getMySchoolsMax(planName);

  // CAM-CATEGORIES-001: CAM categories share the same per-plan cap as Saved
  // Lists (PLAN_LIST_LIMITS) — otherwise a Starter student could bypass the
  // Saved Lists quota entirely via "+ Create New Category...".
  const categoryListLimit: number = PLAN_LIST_LIMITS[planName] ?? 5;
  const namedCategoryCount = React.useMemo(
    () => camCategories.filter((cat) => !cat.isDefault).length,
    [camCategories],
  );
  const atCategoryLimit = namedCategoryCount >= categoryListLimit;

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const loadCollegeLists = React.useCallback(async () => {
    const id = getUserId();
    const tok = getAccessToken();
    if (!id || !tok) return;
    try {
      const res = await collegeServices.getCollegeLists(id);
      setSavedListsData({
        studentLists: (res?.studentLists ?? []) as CollegeListGroup[],
        counselorLists: (res?.counselorLists ?? []) as CollegeListGroup[],
      });
    } catch {
      setSavedListsData({ studentLists: [], counselorLists: [] });
    }
  }, []);

  const loadingData = async (showFullPageLoading = true) => {
    studentId = getUserId(); token = getAccessToken();
    if (token && studentId) {
      if (showFullPageLoading) setLoading(true);
      try {
        const [result] = await Promise.all([
          collegeServices.getMyCollege(studentId),
          loadCollegeLists(),
        ]);
        if (result?.colleges_array) {
          const list = checkEnableParseJSON(result?.colleges_array, []);
          const groups = Array.isArray(list) ? list : [];
          // Counselor "Save to My Colleges" keeps list_group_name as the counselor
          // list (e.g. Reach). Collect is_saved from every group into My Colleges.
          const likedList: any[] = [];
          const admissionMap: Record<string, AdmissionResultUi> = {};
          const seenKeys = new Set<string>();
          groups.forEach((group: CollegeListGroup) => {
            (group.collegeList ?? []).forEach((college: any) => {
              if (!college?.is_saved) return;
              const key = getCollegeKey(college);
              if (key) {
                if (seenKeys.has(key)) return;
                seenKeys.add(key);
                admissionMap[key] = normalizeAdmissionResultUi(
                  college.admission_result,
                );
              }
              likedList.push(college);
            });
          });
          const myGroup: CollegeListGroup = {
            name: MY_COLLEGES_LIST_NAME,
            collegeList: likedList,
          };
          setMyCurrentList([myGroup]);
          setMyLikedCurrentList(
            likedList.sort((a, b) => sortCollegeKey(a) - sortCollegeKey(b)),
          );
          setAdmission(admissionMap);
        }
      } finally {
        if (showFullPageLoading) setLoading(false);
      }
    } else {
      handleLogout();
    }
    if (!studentId) return;
  };

  const loadCamOpenContext = React.useCallback(async () => {
    const id = getUserId();
    const tok = getAccessToken();
    if (!id || !tok) return;
    try {
      const ctx = await collegeServices.getCamOpenContext(id);
      setCamOpenContext({
        requirementsReturnTo: ctx.requirementsReturnTo,
        likelihoodRanges: normalizeLikelihoodRanges(ctx.likelihoodRanges),
      });
    } catch {
      setCamOpenContext(null);
    }
  }, []);

  const loadCamCategories = React.useCallback(async () => {
    const id = getUserId();
    const tok = getAccessToken();
    if (!id || !tok) return;
    try {
      const res: any = await collegeServices.getCamCategories(id);
      setCamCategories(res?.categories ?? []);
      setSchoolCategoryByLegacyId(res?.schoolCategoryByLegacyId ?? {});
      setSchoolCategoryBySchoolId(res?.schoolCategoryBySchoolId ?? {});
      setSchoolMajorByLegacyId(res?.schoolMajorByLegacyId ?? {});
      setSchoolMajorBySchoolId(res?.schoolMajorBySchoolId ?? {});
    } catch {
      setCamCategories([]);
      setSchoolCategoryByLegacyId({});
      setSchoolCategoryBySchoolId({});
      setSchoolMajorByLegacyId({});
      setSchoolMajorBySchoolId({});
    }
  }, []);

  React.useEffect(() => {
    loadingData();
    loadCamOpenContext();
    loadCamCategories();
  }, []);

  /** Canonical CAM schools.id — preferred My Colleges lookup key. */
  const getSchoolId = (college: any): number | null => {
    const raw = college?.school_id;
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  /** GradMap legacy/IPEDS college_id — fallback when school_id is absent. */
  const getLegacyId = (college: any): number | null => {
    const raw = college?.college_id;
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getCollegeMajor = (college: any) => {
    const schoolId = getSchoolId(college);
    const legacyId = getLegacyId(college);
    const fromSchool =
      schoolId != null ? schoolMajorBySchoolId[String(schoolId)] : undefined;
    const fromLegacy =
      legacyId != null ? schoolMajorByLegacyId[String(legacyId)] : undefined;
    // Majors are also embedded on the college row from buildCollegesArray —
    // prefer any source that actually has a Primary Major (avoids empty maps
    // / key mismatches leaving the dropdown on "Select primary major...").
    const embedded = {
      intendedMajor:
        (college?.intended_major as string | null | undefined) ??
        (college?.intendedMajor as string | null | undefined) ??
        null,
      alternateMajor:
        (college?.alternate_major as string | null | undefined) ??
        (college?.alternateMajor as string | null | undefined) ??
        null,
    };
    if (fromSchool?.intendedMajor) return fromSchool;
    if (fromLegacy?.intendedMajor) return fromLegacy;
    if (embedded.intendedMajor) return embedded;
    return fromSchool ?? fromLegacy ?? null;
  };

  const getCollegeCategoryId = (college: any): string | null => {
    const schoolId = getSchoolId(college);
    if (schoolId != null && schoolCategoryBySchoolId[String(schoolId)] !== undefined) {
      return schoolCategoryBySchoolId[String(schoolId)];
    }
    const legacyId = getLegacyId(college);
    if (legacyId != null) return schoolCategoryByLegacyId[String(legacyId)] ?? null;
    return null;
  };

  const getCollegeMajorOptions = (college: any): Array<{ name: string }> => {
    const schoolId = getSchoolId(college);
    if (schoolId != null && majorOptionsByKey[String(schoolId)]) {
      return majorOptionsByKey[String(schoolId)];
    }
    const legacyId = getLegacyId(college);
    if (legacyId != null) return majorOptionsByKey[String(legacyId)] ?? [];
    return [];
  };

  // CAM-CATEGORIES-001: fetch Primary/Alternate Major options for every school
  // currently on "My Schools" so the inline pickers on each card have data.
  React.useEffect(() => {
    const schoolIds = myLikedCurrentList
      .map((c) => getSchoolId(c))
      .filter((id): id is number => id != null && majorOptionsByKey[String(id)] === undefined);
    const legacyIds = myLikedCurrentList
      .map((c) => getLegacyId(c))
      .filter((id): id is number => id != null && majorOptionsByKey[String(id)] === undefined);
    if (schoolIds.length === 0 && legacyIds.length === 0) return;
    collegeServices
      .getMajorOptions(legacyIds, schoolIds)
      .then((options) => {
        setMajorOptionsByKey((prev) => ({ ...prev, ...options }));
      })
      .catch(() => {
        /* non-fatal — pickers just show an empty option list */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myLikedCurrentList]);

  // SPEC_Undecided_Toggle (v1.1) — fetch the current "I'm undecided about my
  // Areas of Interest" flag on mount. Fails silently to `false` (the default).
  React.useEffect(() => {
    const id = getUserId();
    if (!id) return;
    collegeServices
      .getAreasOfInterestUndecided(id)
      .then((value) => setAreasOfInterestUndecided(Boolean(value)))
      .catch(() => {
        /* non-fatal — default to false */
      });
  }, []);

  // Optimistic update: flip UI first, persist, then roll back + toast on
  // failure so a failed PUT cannot look like a saved preference (AC 2).
  const handleAreasOfInterestUndecidedChange = async (next: boolean) => {
    const id = getUserId();
    if (!id) return;
    const previous = areasOfInterestUndecided;
    setAreasOfInterestUndecided(next);
    setAreasUndecidedSaving(true);
    try {
      await collegeServices.saveAreasOfInterestUndecided(id, next);
    } catch {
      setAreasOfInterestUndecided(previous);
      toast.error('Could not save your Areas of Interest preference.');
    } finally {
      setAreasUndecidedSaving(false);
    }
  };

  const toggleSelectSchool = (schoolId: number | null) => {
    if (schoolId == null) return;
    setSelectedSchoolIds((prev) => {
      const next = new Set(prev);
      if (next.has(schoolId)) next.delete(schoolId);
      else next.add(schoolId);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedSchoolIds(new Set());
    setPendingCategoryId('');
  };

  const performMoveToCategory = async (
    categoryId: string | null,
    schoolIds: number[],
  ) => {
    const id = getUserId();
    if (!id || schoolIds.length === 0) return;
    setIsMovingCategory(true);
    try {
      await collegeServices.moveSchoolsToCamCategory(id, [], categoryId, schoolIds);
      await loadCamCategories();
      clearSelection();
      toast.success('Schools moved.');
    } catch {
      toast.error('Unable to move schools to category. Please try again.');
    } finally {
      setIsMovingCategory(false);
    }
  };

  /**
   * CAM-CATEGORIES-001: Primary/Alternate Major fields live inline on every
   * "My Schools" card (mirrors CAM's Organize Your Schools layout) and save on
   * change. CAM requires a Primary Major before a school can be organized into
   * a category, so Move is blocked (with a toast) until it's filled in on the card.
   */
  const handleMoveSelectedToCategory = async (categoryId: string | null) => {
    if (selectedSchoolIds.size === 0) return;
    const schoolIds = Array.from(selectedSchoolIds);

    if (categoryId == null) {
      // "Remove" back to Uncategorized never requires a major.
      await performMoveToCategory(null, schoolIds);
      return;
    }

    if (!areasOfInterestUndecided) {
      const missing = schoolIds.filter(
        (schoolId) => !schoolMajorBySchoolId[String(schoolId)]?.intendedMajor,
      );
      if (missing.length > 0) {
        toast.error(
          'Set an Area of Interest on the card for each selected school before moving it to a category.',
        );
        return;
      }
    }

    await performMoveToCategory(categoryId, schoolIds);
  };

  const handleSaveSchoolMajor = async (
    college: any,
    primaryMajor: string,
    alternateMajor: string | null,
  ) => {
    const id = getUserId();
    const schoolId = getSchoolId(college);
    const legacyId = getLegacyId(college);
    if (!id || !primaryMajor || (schoolId == null && legacyId == null)) return;
    try {
      await collegeServices.setSchoolMajor(id, legacyId, primaryMajor, alternateMajor, schoolId);
      if (schoolId != null) {
        setSchoolMajorBySchoolId((prev) => ({
          ...prev,
          [String(schoolId)]: { intendedMajor: primaryMajor, alternateMajor },
        }));
      }
      if (legacyId != null) {
        setSchoolMajorByLegacyId((prev) => ({
          ...prev,
          [String(legacyId)]: { intendedMajor: primaryMajor, alternateMajor },
        }));
      }
    } catch {
      toast.error('Unable to save major. Please try again.');
    }
  };

  const handleCreateCategoryAndMove = async () => {
    if (atCategoryLimit) {
      toast.error(
        isStarter
          ? `Upgrade to add more than ${categoryListLimit} category on the Starter plan.`
          : `You've reached your plan's limit of ${categoryListLimit} categories. Upgrade for more.`,
      );
      return;
    }
    const name = window.prompt('New category name')?.trim();
    if (!name) return;
    const id = getUserId();
    if (!id) return;
    setIsMovingCategory(true);
    try {
      const color =
        CATEGORY_COLOR_PALETTE[camCategories.length % CATEGORY_COLOR_PALETTE.length];
      const created = await collegeServices.createCamCategory(id, name, color);
      await loadCamCategories();
      toast.success('Category created.');
      if (selectedSchoolIds.size > 0) {
        setIsMovingCategory(false);
        await handleMoveSelectedToCategory(created.id);
        return;
      }
    } catch {
      toast.error('Unable to create category. Please try again.');
    } finally {
      setIsMovingCategory(false);
    }
  };

  const handleSavingData = async (savedData: any) => {
    studentId = getUserId(); token = getAccessToken();
    if (token && studentId) {
      setSaving(true);
      try {
        const groups = Array.isArray(savedData) ? savedData : [];
        const myGroup =
          groups.find((g: CollegeListGroup) => g.name === MY_COLLEGES_LIST_NAME) ??
          myCurrentList[0] ??
          { name: MY_COLLEGES_LIST_NAME, collegeList: [] };
        await collegeServices.updatetMyCollege(
          studentId,
          JSON.stringify([myGroup]),
        );
        await loadingData(false);
        await loadCamOpenContext();
      } finally { setSaving(false); }
    } else { handleLogout(); }
  };

  const handleCheckedItem = (nameSelected: string) => {
    const newList = myCurrentList.map((list) => ({
      name: list.name,
      collegeList: list.collegeList.map((i) =>
        i.name === nameSelected ? { ...i, is_saved: false } : { ...i }
      ),
    }));
    setMyCurrentList(newList);
    handleSavingData(newList);
  };

  // Persist "My Take" into colleges_array → backend syncs it to CAM.
  const handleSetMyTake = (college: any, takeKey: MyTake) => {
    const label = takeLabelFromKey(takeKey);
    const newList = myCurrentList.map((list) => ({
      name: list.name,
      collegeList: list.collegeList.map((i) =>
        isSameCollege(i, college) ? { ...i, student_ranking: label } : { ...i }
      ),
    }));
    setMyCurrentList(newList);
    setMyLikedCurrentList((prev) =>
      prev.map((c) => (isSameCollege(c, college) ? { ...c, student_ranking: label } : c))
    );
    handleSavingData(newList);
  };

  const handleSetAdmission = (college: any, admissionKey: AdmissionResultUi) => {
    const key = getCollegeKey(college);
    if (!key) {
      toast.error('Unable to update application status for this school. Please refresh and try again.');
      return;
    }
    const newList = myCurrentList.map((list) => ({
      name: list.name,
      collegeList: list.collegeList.map((i) =>
        isSameCollege(i, college) ? { ...i, admission_result: admissionKey } : { ...i }
      ),
    }));
    setMyCurrentList(newList);
    setMyLikedCurrentList((prev) =>
      prev.map((c) =>
        isSameCollege(c, college) ? { ...c, admission_result: admissionKey } : c
      )
    );
    setAdmission((p) => ({ ...p, [key]: admissionKey }));
    handleSavingData(newList);
  };

  const [listCounselorConnection, setListCounselorConnection] = React.useState<any[]>([]);
  React.useEffect(() => {
    studentId = getUserId(); token = getAccessToken();
    if (token && studentId) {
      counselorServices.getCounselors(studentId).then((res: any[]) => {
        setListCounselorConnection(res ?? []);
      });
    } else { handleLogout(); }

    console.log('listCounselorConnection:', listCounselorConnection);
  }, [studentId, token]);

  const [forceRenderKey, setForceRenderKey] = React.useState(0);

  // CAM-CATEGORIES-001: group "My Schools" by CAM category (named categories in
  // CAM's own order, Uncategorized always last), mirroring CAM's own grouping.
  const uncategorizedCat = React.useMemo(
    () => camCategories.find((cat) => cat.isDefault) ?? null,
    [camCategories],
  );

  const resolveGroupInfo = React.useCallback(
    (college: any): { key: string; order: number; label: string; color: string } => {
      const catId = getCollegeCategoryId(college);
      const isUncategorized =
        catId == null || (uncategorizedCat != null && catId === uncategorizedCat.id);
      if (isUncategorized) {
        return {
          key: '__uncategorized__',
          order: Number.MAX_SAFE_INTEGER,
          label: uncategorizedCat?.name ?? 'Uncategorized',
          color: uncategorizedCat?.color ?? '#9CA3AF',
        };
      }
      const idx = camCategories.findIndex((cat) => cat.id === catId);
      const cat = idx !== -1 ? camCategories[idx] : null;
      return {
        key: cat?.id ?? '__uncategorized__',
        order: idx !== -1 ? idx : Number.MAX_SAFE_INTEGER,
        label: cat?.name ?? 'Uncategorized',
        color: cat?.color ?? '#9CA3AF',
      };
    },
    [camCategories, schoolCategoryByLegacyId, schoolCategoryBySchoolId, uncategorizedCat],
  );

  const onDragEnd = async (result: any) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    // Draggable indices refer to positions in the grouped `visibleColleges`
    // list, not the raw `myLikedCurrentList` — resolve by stable college key,
    // then reorder without swapping identity fields (old code swapped `id`s and
    // corrupted per-school admission matching).
    const startItem = visibleColleges[result.source.index];
    const endItem = visibleColleges[result.destination.index];
    if (!startItem || !endItem) return;
    const actualSourceIndex = myLikedCurrentList.findIndex((c) => isSameCollege(c, startItem));
    const actualDestIndex = myLikedCurrentList.findIndex((c) => isSameCollege(c, endItem));
    if (actualSourceIndex === -1 || actualDestIndex === -1) return;
    const dataList = reorder(myLikedCurrentList, actualSourceIndex, actualDestIndex);
    const newMyCurrentList = myCurrentList.map((list) => {
      if (list.name !== MY_COLLEGES_LIST_NAME) return list;
      const unsaved = (list.collegeList ?? []).filter((c) => !c.is_saved);
      return { name: list.name, collegeList: [...dataList, ...unsaved] };
    });
    if (studentId) {
      setSaving(true);
      try { await collegeServices.updatetMyCollege(studentId, JSON.stringify(newMyCurrentList)); }
      finally { setSaving(false); }
    }
    setMyCurrentList(newMyCurrentList);
    setMyLikedCurrentList([...dataList]);
    setForceRenderKey(forceRenderKey + 1);
  };

  const visibleColleges = React.useMemo(() => {
    const base = showNotApplying
      ? myLikedCurrentList
      : myLikedCurrentList.filter((c) => {
          const key = getCollegeKey(c);
          return (key ? admission[key] : undefined) !== 'not-applying';
        });
    // Stable sort: named CAM categories in CAM's own order, Uncategorized last.
    return [...base].sort((a, b) => resolveGroupInfo(a).order - resolveGroupInfo(b).order);
  }, [myLikedCurrentList, admission, showNotApplying, resolveGroupInfo]);

  const groupCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of visibleColleges) {
      const key = resolveGroupInfo(c).key;
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [visibleColleges, resolveGroupInfo]);

  const admissionSummary = React.useMemo(
    () => computeAdmissionSummary(myLikedCurrentList, admission),
    [myLikedCurrentList, admission],
  );

  const likelihoodCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const bucket of LIKELIHOOD_BALANCE_BUCKETS) {
      counts[bucket.rangeKey] = 0;
    }
    for (const college of myLikedCurrentList) {
      const take = takeKeyFromValue(college.student_ranking);
      if (!take) continue;
      const rangeKey = MY_TAKE_TO_RANGE_KEY[take];
      if (rangeKey) counts[rangeKey] = (counts[rangeKey] ?? 0) + 1;
    }
    return counts;
  }, [myLikedCurrentList]);

  const handleOpenInCam = async (college: any) => {
    let hasToken = !!getAccessToken();
    if (!hasToken) {
      notifyCamSessionExpired();
      return;
    }

    // Token is present but may already be expired/revoked server-side —
    // confirm with the backend before handing it off to CAM.
    const sessionValid = await isGradMapSessionValid();
    if (!sessionValid) {
      notifyCamSessionExpired();
      return;
    }

    const collegeKey = getCollegeKey(college);
    const admissionKey =
      (collegeKey ? admission[collegeKey] : undefined) ?? DEFAULT_ADMISSION_RESULT;
    if (admissionKey === 'not-applying') {
      toast.error('This school is marked Not applying. Update admission status to open in CAM.');
      return;
    }
    if (!getCamBaseUrl()) {
      toast.error('CAM is not configured. Please contact support.');
      return;
    }
    if (isStarter) {
      history.push(`${routers.settings.path}?active-tab=5`);
      return;
    }
    const studentIdNow = getUserId();
    if (!studentIdNow) return;
    try {
      const legacyId =
        college.college_id != null ? Number(college.college_id) : undefined;
      const ctx = await collegeServices.getCamOpenContext(
        studentIdNow,
        legacyId != null && !Number.isNaN(legacyId) ? legacyId : undefined,
      );
      const returnTo = ctx.requirementsReturnTo ?? camOpenContext?.requirementsReturnTo ?? '/';
      if (!openInCam(returnTo)) {
        showCamPopupBlockedToast(toast.error);
      }
    } catch {
      const fallback = camOpenContext?.requirementsReturnTo ?? '/';
      if (!openInCam(fallback)) {
        showCamPopupBlockedToast(toast.error);
      }
    }
  };

  if (loading) return <LoadingContent />;

  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'auto', height: 'calc(100vh - 56px)', flexDirection: isMobile ? 'column' : 'row' }}>
      {/* ── Main column ──────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, p: isMobile ? 2 : 4, overflowY: 'auto' }}>
        <SavingIndicator saving={saving} />

        {/* ── Free plan banner ────────────────────────────────────── */}
        {isStarter && (
          <a href="/settings?active-tab=5" className="gm-free-banner" style={{ marginBottom: 20 }}>
            <span>💡 <strong>You're on the free plan</strong> — Upgrade for full CAM access, unlimited lists, and counselor messaging.</span>
            <span className="gm-free-banner-arrow">→</span>
          </a>
        )}

        {/* ── Header ────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
          <h2 className="gm-page-title" style={{ fontSize: isMobile ? 22 : 28, margin: 0 }}>My Colleges</h2>
          <span style={{ fontSize: 14, fontWeight: 500, color: BRAND_NAVY, fontFamily: "'DM Sans', sans-serif" }}>
            {Number.isFinite(mySchoolsMax)
              ? `${myLikedCurrentList.length} / ${mySchoolsMax} selected`
              : `${myLikedCurrentList.length} selected`}
          </span>
        </Box>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 24, fontFamily: "'DM Sans', sans-serif" }}>
          Track your schools, set your strategy, and see where you stand.
        </p>

        {/* ── Tabs ──────────────────────────────────────────────── */}
        <div className="gm-tabs-list">
          <button className={`gm-tab${activeTab === 'my-schools' ? ' active' : ''}`} onClick={() => setActiveTab('my-schools')}>My Schools</button>
          <button className={`gm-tab${activeTab === 'saved-lists' ? ' active' : ''}`} onClick={() => setActiveTab('saved-lists')}>Saved Lists</button>
        </div>

        {/* ── TAB: My Schools ─────────────────────────────────── */}
        {activeTab === 'my-schools' && (
          <>
            {isStarter && Number.isFinite(mySchoolsMax) && myLikedCurrentList.length >= mySchoolsMax && (
              <a
                href="/settings?active-tab=5"
                className="gm-free-banner"
                style={{ marginBottom: 16 }}
              >
                <span>
                  🔒 <strong>You've reached your {mySchoolsMax}-school limit</strong> on the free plan — upgrade for unlimited schools.
                </span>
                <span className="gm-free-banner-arrow">→</span>
              </a>
            )}

            {/* SPEC_Undecided_Toggle (v1.1) — student-level "I'm undecided" flag
                for Areas of Interest. Visible above the tracking summary. When
                checked, per-school Area of Interest fields default to Exploring
                and major-dependent tasks are gated until it's unchecked. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                padding: '12px 16px',
                background: '#F0F9FF',
                border: '1px solid #BAE6FD',
                borderRadius: 2,
                marginBottom: 2,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <input
                type="checkbox"
                id="areas-of-interest-undecided"
                checked={areasOfInterestUndecided}
                disabled={areasUndecidedSaving}
                onChange={(e) => handleAreasOfInterestUndecidedChange(e.target.checked)}
                style={{
                  width: 18,
                  height: 18,
                  marginTop: 2,
                  accentColor: BRAND_NAVY,
                  cursor: areasUndecidedSaving ? 'wait' : 'pointer',
                }}
              />
              <label
                htmlFor="areas-of-interest-undecided"
                style={{
                  cursor: areasUndecidedSaving ? 'wait' : 'pointer',
                  color: '#1F2937',
                  fontSize: 14,
                  lineHeight: 1.4,
                }}
              >
                <strong>I'm undecided about my Areas of Interest right now</strong>
                <span
                  style={{
                    display: 'block',
                    color: '#6B7280',
                    fontSize: 12,
                    marginTop: 2,
                  }}
                >
                  Skip picking a major or interest area for now — you can set them anytime. Major-specific tasks will unlock once you choose.
                </span>
              </label>
              {areasUndecidedSaving && (
                <span
                  style={{
                    color: '#6B7280',
                    fontSize: 12,
                    marginLeft: 'auto',
                    alignSelf: 'center',
                  }}
                  aria-live="polite"
                >
                  Saving…
                </span>
              )}
            </Box>

            {myLikedCurrentList.length > 0 && (
              <div className="gm-admission-summary">
                {isMobile && (
                  <>
                    <span>
                      <strong>{myLikedCurrentList.length}</strong> in list
                    </span>
                    <span className="gm-admission-summary-divider" aria-hidden />
                  </>
                )}
                <span>
                  Tracking: <strong>{admissionSummary.tracking}</strong>
                </span>
                <span className="gm-admission-summary-divider" aria-hidden />
                <span>
                  Submitted: <strong>{admissionSummary.submitted}</strong>
                </span>
                <span className="gm-admission-summary-divider" aria-hidden />
                <span>
                  Accepted: <strong>{admissionSummary.accepted}</strong>
                </span>
              </div>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
                <input
                  type="checkbox"
                  checked={showNotApplying}
                  onChange={(e) => setShowNotApplying(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: BRAND_NAVY }}
                />
                Show schools I'm not applying to
              </label>
            </Box>

            {myLikedCurrentList.length === 0 ? (
              <div className="gm-empty-state" style={{ maxWidth: 460, margin: '0 auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}><IconGraduationCap /></Box>
                <h3 className="gm-display" style={{ fontWeight: 700, fontSize: 18, color: '#111827', marginBottom: 8 }}>No colleges yet</h3>
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>Start building your college list from College Search.</p>
                <button
                  className="gm-btn-orange"
                  style={{ borderRadius: 8, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  onClick={() => history.push(routers.collegeSearch.path)}
                >
                  <IconSearch /> Go to College Search
                </button>
              </div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd} key={forceRenderKey}>
                <Droppable droppableId="list">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {visibleColleges.map((miniItem: any, index: number) => {
                        const groupInfo = resolveGroupInfo(miniItem);
                        const prevGroupKey = index > 0 ? resolveGroupInfo(visibleColleges[index - 1]).key : null;
                        const showGroupHeader = groupInfo.key !== prevGroupKey;
                        const collegeKey = getCollegeKey(miniItem);
                        const admissionResult =
                          (collegeKey ? admission[collegeKey] : undefined) ??
                          DEFAULT_ADMISSION_RESULT;
                        return (
                          <React.Fragment key={collegeKey ?? `idx-${index}`}>
                            {showGroupHeader && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mt: index === 0 ? 0 : 1,
                                  mb: 0.5,
                                  px: 0.5,
                                }}
                              >
                                <span style={{ width: 8, height: 8, borderRadius: 9999, background: groupInfo.color, flexShrink: 0 }} />
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#374151', fontFamily: "'DM Sans', sans-serif" }}>
                                  {groupInfo.label}
                                </span>
                                <span style={{ fontSize: 12, color: '#9CA3AF', fontFamily: "'DM Sans', sans-serif" }}>
                                  ({groupCounts[groupInfo.key] ?? 0})
                                </span>
                                <Box sx={{ flex: 1, height: '1px', background: '#e5e7eb', ml: 0.5 }} />
                              </Box>
                            )}
                            <Draggable
                              draggableId={`college-${collegeKey ?? index}`}
                              index={index}
                              isDragDisabled={isMobile}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    opacity: snapshot.isDragging ? 0.85 : 1,
                                  }}
                                >
                                  <MySchoolCard
                                    college={miniItem}
                                    myTake={takeKeyFromValue(miniItem.student_ranking)}
                                    onSetMyTake={(t) => handleSetMyTake(miniItem, t)}
                                    admissionResult={admissionResult}
                                    onSetAdmission={(a) => handleSetAdmission(miniItem, a)}
                                    isPaid={!isStarter}
                                    onRemove={() => handleCheckedItem(miniItem.name)}
                                    onUpgrade={() => history.push(`${routers.settings.path}?active-tab=5`)}
                                    onOpenInCam={() => handleOpenInCam(miniItem)}
                                    camOpenDisabled={admissionResult === 'not-applying'}
                                    dragHandleProps={isMobile ? undefined : provided.dragHandleProps}
                                    // CAM-CATEGORIES-001: organizing into CAM categories requires
                                    // CAM access itself — locked for Starter, same as "Open in CAM".
                                    categoryFeatureLocked={isStarter}
                                    onUnlockCategoryFeature={() => history.push(`${routers.settings.path}?active-tab=5`)}
                                    selected={
                                      !isStarter &&
                                      getSchoolId(miniItem) != null &&
                                      selectedSchoolIds.has(getSchoolId(miniItem) as number)
                                    }
                                    onToggleSelect={isStarter ? undefined : () => toggleSelectSchool(getSchoolId(miniItem))}
                                    category={
                                      isStarter
                                        ? null
                                        : camCategories.find(
                                            (cat) => cat.id === getCollegeCategoryId(miniItem),
                                          ) ?? null
                                    }
                                    majorOptions={getCollegeMajorOptions(miniItem)}
                                    intendedMajor={getCollegeMajor(miniItem)?.intendedMajor ?? null}
                                    alternateMajor={getCollegeMajor(miniItem)?.alternateMajor ?? null}
                                    areasOfInterestUndecided={areasOfInterestUndecided}
                                    onSaveMajor={
                                      isStarter
                                        ? undefined
                                        : (primary, alternate) =>
                                            handleSaveSchoolMajor(miniItem, primary, alternate)
                                    }
                                  />
                                </div>
                              )}
                            </Draggable>
                          </React.Fragment>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            {/* CAM-CATEGORIES-001: bulk category action bar, mirrors CAM's
                Organize Your Schools action bar (Move to category / Remove / Clear). */}
            {selectedSchoolIds.size > 0 && (
              <Box
                sx={{
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  bottom: 24,
                  zIndex: 20,
                  display: 'flex',
                  justifyContent: 'center',
                  px: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                    p: 1.5,
                    maxWidth: 720,
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: 9999,
                        background: BRAND_NAVY,
                        color: '#fff',
                        fontSize: 12,
                      }}
                    >
                      {selectedSchoolIds.size}
                    </span>
                    {selectedSchoolIds.size === 1 ? 'school' : 'schools'} selected
                  </Box>

                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <select
                      value={pendingCategoryId}
                      disabled={isMovingCategory}
                      style={{ fontSize: 13, padding: '7px 8px', borderRadius: 6, border: '1px solid #d1d5db', minWidth: 170 }}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'create-new') {
                          if (atCategoryLimit) {
                            toast.error(
                              isStarter
                                ? `Upgrade to add more than ${categoryListLimit} category on the Starter plan.`
                                : `You've reached your plan's limit of ${categoryListLimit} categories. Upgrade for more.`,
                            );
                          } else {
                            void handleCreateCategoryAndMove();
                          }
                          setPendingCategoryId('');
                        } else {
                          setPendingCategoryId(value);
                        }
                      }}
                    >
                      <option value="">Move to category...</option>
                      {camCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                      <option value="create-new" disabled={atCategoryLimit}>
                        {atCategoryLimit
                          ? `🔒 ${isStarter ? 'Upgrade to add more categories' : 'Category limit reached'}`
                          : '+ Create New Category...'}
                      </option>
                    </select>

                    <button
                      type="button"
                      disabled={isMovingCategory || !pendingCategoryId}
                      onClick={() => void handleMoveSelectedToCategory(pendingCategoryId)}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '7px 12px',
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        background: '#fff',
                        cursor: isMovingCategory || !pendingCategoryId ? 'not-allowed' : 'pointer',
                        opacity: isMovingCategory || !pendingCategoryId ? 0.5 : 1,
                      }}
                    >
                      Move
                    </button>

                    <button
                      type="button"
                      disabled={isMovingCategory}
                      onClick={() => void handleMoveSelectedToCategory(null)}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '7px 12px',
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        background: '#fff',
                        cursor: isMovingCategory ? 'not-allowed' : 'pointer',
                        opacity: isMovingCategory ? 0.5 : 1,
                      }}
                    >
                      Remove
                    </button>

                    <button
                      type="button"
                      onClick={clearSelection}
                      aria-label="Clear selection"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}
                    >
                      <IconX size={16} />
                    </button>
                  </Box>
                </Box>
              </Box>
            )}

          </>
        )}

        {/* ── TAB: Saved Lists ─────────────────────────────────── */}
        {activeTab === 'saved-lists' && (
          <SavedListsTab
            studentId={Number(studentId)}
            studentLists={savedListsData.studentLists}
            counselorLists={savedListsData.counselorLists}
            savedCollegeIds={myLikedCurrentList.map((c) => c.id)}
            onRefreshLists={loadCollegeLists}
            onReloadMyColleges={() => loadingData(false)}
            onViewSearch={() => history.push(routers.collegeSearch.path)}
            isStarter={isStarter}
            maxLists={PLAN_LIST_LIMITS[planName] ?? 5}
            mySchoolsMax={mySchoolsMax}
            onOpenInCam={(college) => void handleOpenInCam(college)}
            onUpgrade={() => history.push(`${routers.settings.path}?active-tab=5`)}
          />
        )}
      </Box>

      {/* ── Right Sidebar — Likelihood Balance ──────────────────────── */}
      {!isMobile && (
        <Box
          sx={{
            width: 280,
            borderLeft: '1px solid #e5e7eb',
            bgcolor: 'white',
            p: 3,
            overflowY: 'auto',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
            <h3 className="gm-display" style={{ fontWeight: 700, fontSize: 15, color: BRAND_NAVY, margin: 0 }}>Likelihood Balance</h3>
            <span style={{ fontSize: 11, fontWeight: 700, width: 22, height: 22, borderRadius: '50%', background: BRAND_NAVY, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {myLikedCurrentList.length}
            </span>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {LIKELIHOOD_BALANCE_BUCKETS.map((b) => {
              const count = likelihoodCounts[b.rangeKey] ?? 0;
              const range = likelihoodRanges[b.rangeKey] ?? { min: 0, max: 0 };
              const rangeLabel = formatLikelihoodRange(range.min, range.max);
              return (
                <div key={b.rangeKey} className="gm-likelihood-row">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>{b.label}</span>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 6,
                        border: '1px solid #e5e7eb',
                        background: count > 0 ? '#f0fdf4' : '#f9fafb',
                        color: count > 0 ? '#166534' : '#6b7280',
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {count}
                    </span>
                    <span style={{ fontSize: 12, color: '#6B7280', fontFamily: "'DM Sans', sans-serif" }}>
                      / {rangeLabel}
                    </span>
                  </Box>
                </div>
              );
            })}
          </Box>

          <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #e5e7eb' }}>
            <p style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
              Guidelines match your counselor&apos;s CAM school list settings (e.g. 0/0-0 until ranges are configured).
            </p>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── My School Card ─────────────────────────────────────────────────────────────
function MySchoolCard({
  college: c, myTake, onSetMyTake, admissionResult, onSetAdmission, isPaid, onRemove, onUpgrade, onOpenInCam, camOpenDisabled, dragHandleProps,
  selected, onToggleSelect, category,
  majorOptions, intendedMajor, alternateMajor, onSaveMajor,
  categoryFeatureLocked, onUnlockCategoryFeature,
  areasOfInterestUndecided = false,
}: {
  college: any;
  myTake: MyTake | null;
  onSetMyTake: (t: MyTake) => void;
  admissionResult: AdmissionResultUi;
  onSetAdmission: (a: AdmissionResultUi) => void;
  isPaid: boolean;
  onRemove: () => void;
  onUpgrade: () => void;
  onOpenInCam: () => void;
  camOpenDisabled?: boolean;
  dragHandleProps?: any;
  selected?: boolean;
  onToggleSelect?: () => void;
  category?: { id: string; name: string; color: string; isDefault: boolean } | null;
  majorOptions?: Array<{ name: string }>;
  intendedMajor?: string | null;
  alternateMajor?: string | null;
  areasOfInterestUndecided?: boolean;
  onSaveMajor?: (primaryMajor: string, alternateMajor: string | null) => void;
  /** CAM-CATEGORIES-001: organizing into CAM categories requires CAM access —
   * locked for Starter, same reasoning as "Open in CAM". */
  categoryFeatureLocked?: boolean;
  onUnlockCategoryFeature?: () => void;
}) {
  const isNotApplying = admissionResult === 'not-applying';
  const majorList = majorOptions ?? [];
  const [primaryOtherMode, setPrimaryOtherMode] = React.useState(
    !!intendedMajor && !majorList.some((m) => m.name === intendedMajor),
  );
  const [alternateOtherMode, setAlternateOtherMode] = React.useState(
    !!alternateMajor && !majorList.some((m) => m.name === alternateMajor),
  );
  // Sync when CAM majors / options arrive after first paint (initial useState
  // only ran with empty intendedMajor + empty majorList).
  React.useEffect(() => {
    setPrimaryOtherMode(
      !!intendedMajor && !majorList.some((m) => m.name === intendedMajor),
    );
  }, [intendedMajor, majorOptions]);
  React.useEffect(() => {
    setAlternateOtherMode(
      !!alternateMajor && !majorList.some((m) => m.name === alternateMajor),
    );
  }, [alternateMajor, majorOptions]);
  const initials = getInitials(c.name || '');
  const takeCfg = myTake ? MY_TAKE_OPTIONS.find((o) => o.key === myTake) : null;
  const counselorTakeKey = takeKeyFromValue(c.counselor_ranking);
  const counselorCfg = counselorTakeKey
    ? MY_TAKE_OPTIONS.find((o) => o.key === counselorTakeKey)
    : null;
  const admissionLabel = admissionLabelFor(admissionResult);
  const admissionChip = admissionChipStyleFor(admissionResult);

  return (
    <div className="gm-college-card" style={{ opacity: isNotApplying ? 0.45 : 1, display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* ROW 1 — Identity */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            aria-label={`Select ${c.name}`}
            style={{ width: 16, height: 16, marginTop: 12, flexShrink: 0, cursor: 'pointer' }}
          />
        )}
        {dragHandleProps && (
          <span {...dragHandleProps} style={{ cursor: 'grab', flexShrink: 0, marginTop: 10, color: '#9ca3af' }}>
            <IconDragHandle />
          </span>
        )}
        <div className="gm-college-avatar">{initials}</div>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box>
              <h4 className="gm-display" style={{ fontWeight: 600, fontSize: 15, color: '#111827', margin: 0, lineHeight: 1.3, wordBreak: 'break-word' }}>
                {c.name}
                {isNotApplying && (
                  <span className="gm-type-chip" style={{ marginLeft: 8, fontSize: 9 }}>Not applying</span>
                )}
                {category && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 9999,
                      background: `${category.color}1A`,
                      color: category.color,
                      border: `1px solid ${category.color}55`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {category.name}
                  </span>
                )}
              </h4>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>
                {[c.city, c.state].filter(Boolean).join(', ')}
              </p>
            </Box>
            <button
              onClick={onRemove}
              aria-label="Remove school"
              style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: '#9ca3af', flexShrink: 0, borderRadius: 4 }}
            >
              <IconX size={15} />
            </button>
          </Box>
        </Box>
      </Box>

      {/* ROW 2 — Actions: student info left, counselor info right */}
      <Box sx={{ mt: 1.5, pl: dragHandleProps ? '70px' : '52px', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FieldLabelGroup label="Likelihood:">
            <LightDropdown
              buttonClass="gm-my-take-btn"
              menuClassName="gm-my-take-menu"
              label={takeCfg ? takeCfg.label : 'Select'}
              buttonStyle={
                takeCfg
                  ? { background: takeCfg.bg, color: takeCfg.text, borderColor: takeCfg.border }
                  : undefined
              }
            >
              {(close) => (
                <>
                  {MY_TAKE_OPTIONS.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      className="gm-my-take-menu-item"
                      onClick={() => { onSetMyTake(o.key); close(); }}
                    >
                      <span className="gm-my-take-menu-item-label">{o.label}</span>
                    </button>
                  ))}
                </>
              )}
            </LightDropdown>
          </FieldLabelGroup>

          <FieldLabelGroup label="Application Status:">
            <LightDropdown
              buttonClass="gm-admission-btn"
              menuClassName="gm-admission-menu"
              label={admissionLabel}
              buttonStyle={{
                background: admissionChip.background,
                color: admissionChip.color,
                borderColor: admissionChip.border,
                fontWeight: 600,
                borderRadius: 9999,
              }}
            >
              {(close) => (
                <>
                  {ADMISSION_OPTIONS.map((o) => {
                    const chip = admissionChipStyleFor(o.key);
                    return (
                      <button
                        key={o.key}
                        onClick={() => { onSetAdmission(o.key); close(); }}
                        style={{
                          width: '100%',
                          background: 'none',
                          border: 'none',
                          padding: '7px 12px',
                          fontSize: 12,
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#f9fafb'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'none'; }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 9999,
                            fontSize: 11,
                            fontWeight: 600,
                            background: chip.background,
                            color: chip.color,
                            border: `1px solid ${chip.border}`,
                          }}
                        >
                          {o.label}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
            </LightDropdown>
          </FieldLabelGroup>
        </Box>

        {/* Counselor take + Open in CAM — right-aligned */}
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FieldLabelGroup
            label="Counselor's Take:"
            title="Your counselor's assessment. They control this — you can't change it."
          >
            <span
              className="gm-my-take-btn gm-counselor-take-chip"
              style={{
                background: counselorCfg ? counselorCfg.bg : COUNSELOR_TAKE_EMPTY.bg,
                color: counselorCfg ? counselorCfg.text : COUNSELOR_TAKE_EMPTY.text,
                borderColor: counselorCfg ? counselorCfg.border : COUNSELOR_TAKE_EMPTY.border,
              }}
            >
              {counselorCfg ? counselorCfg.label : COUNSELOR_TAKE_EMPTY.label}
            </span>
          </FieldLabelGroup>

          {isPaid ? (
            <button
              type="button"
              className="gm-btn-orange"
              style={{
                fontSize: 12,
                borderRadius: 6,
                padding: '6px 12px',
                opacity: camOpenDisabled ? 0.5 : 1,
                cursor: camOpenDisabled ? 'not-allowed' : 'pointer',
              }}
              disabled={camOpenDisabled}
              title={camOpenDisabled ? 'Not applying — change admission status to open in CAM' : undefined}
              onClick={onOpenInCam}
            >
              Open in CAM
            </button>
          ) : (
            <button
              className="gm-btn-orange"
              style={{ fontSize: 12, borderRadius: 6, padding: '6px 12px', opacity: 0.6 }}
              onClick={onUpgrade}
              title="Available with paid plan — upgrade to unlock"
            >
              🔒 Open in CAM
            </button>
          )}
        </Box>
      </Box>

      {/* ROW 3 (Starter) — category organizing requires CAM access, so it's
          locked here the same way "Open in CAM" is, with an Upgrade link. */}
      {categoryFeatureLocked && (
        <Box
          sx={{
            mt: 1.5,
            pl: dragHandleProps ? '70px' : '52px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <span style={{ fontSize: 12, color: '#9CA3AF', fontFamily: "'DM Sans', sans-serif" }}>
            🔒 Organize into categories &amp; set Area of Interest — available with paid plans.
          </span>
          {onUnlockCategoryFeature && (
            <button
              type="button"
              onClick={onUnlockCategoryFeature}
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: BRAND_ORANGE,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Upgrade
            </button>
          )}
        </Box>
      )}

      {/* ROW 3 — Primary/Alternate Major (custom light dropdowns; native <select>
          menus cannot be styled and inherit OS dark mode). */}
      {onSaveMajor && (
        <Box
          sx={{
            mt: 1.5,
            pl: dragHandleProps ? '70px' : '52px',
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', fontFamily: "'DM Sans', sans-serif" }}>
              Area of Interest (Select &apos;Other&apos; to manually enter)
              {areasOfInterestUndecided ? (
                <>
                  :{' '}
                  <span style={{ color: '#0284c7', fontWeight: 600 }}>Still Exploring</span>
                </>
              ) : (
                <span style={{ color: '#dc2626' }}> *</span>
              )}
            </label>
            <MajorSelectDropdown
              ariaLabel="Area of interest"
              placeholder={areasOfInterestUndecided ? 'Set anytime when ready…' : 'Select area of interest...'}
              value={primaryOtherMode ? '__other__' : (intendedMajor ?? '')}
              options={majorList}
              emptyHighlight={!areasOfInterestUndecided && !intendedMajor}
              allowNone={false}
              allowOther
              onChange={(value) => {
                if (value === '__other__') {
                  setPrimaryOtherMode(true);
                  return;
                }
                setPrimaryOtherMode(false);
                onSaveMajor(value, alternateMajor ?? null);
              }}
            />
            {primaryOtherMode && (
              <>
                <input
                  type="text"
                  defaultValue={intendedMajor && !majorList.some((m) => m.name === intendedMajor) ? intendedMajor : ''}
                  placeholder="Enter the area of interest you will apply into"
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value) onSaveMajor(value, alternateMajor ?? null);
                  }}
                  style={{ width: '100%', fontSize: 13, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', marginTop: 6, boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif" }}
                />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>
                  We&apos;ll review this major and add it to this school&apos;s list if approved. Your application still uses the name you entered.
                </div>
              </>
            )}
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', fontFamily: "'DM Sans', sans-serif" }}>
              Alternate Area of Interest (Select &apos;Other&apos; to manually enter):
            </label>
            <MajorSelectDropdown
              ariaLabel="Alternate area of interest"
              placeholder="None"
              value={alternateOtherMode ? '__other__' : (alternateMajor ?? '')}
              options={majorList}
              disabled={!intendedMajor}
              allowNone
              allowOther
              onChange={(value) => {
                if (value === '__other__') {
                  setAlternateOtherMode(true);
                  return;
                }
                setAlternateOtherMode(false);
                onSaveMajor(intendedMajor ?? '', value || null);
              }}
            />
            {alternateOtherMode && (
              <>
                <input
                  type="text"
                  defaultValue={alternateMajor && !majorList.some((m) => m.name === alternateMajor) ? alternateMajor : ''}
                  placeholder="Enter alternate area of interest (optional)"
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    onSaveMajor(intendedMajor ?? '', value || null);
                  }}
                  style={{ width: '100%', fontSize: 13, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', marginTop: 6, boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif" }}
                />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>
                  We&apos;ll review this major and add it to this school&apos;s list if approved. Your application still uses the name you entered.
                </div>
              </>
            )}
          </Box>
        </Box>
      )}
    </div>
  );
}

function MajorSelectDropdown({
  value,
  options,
  placeholder,
  ariaLabel,
  disabled = false,
  emptyHighlight = false,
  allowNone = false,
  allowOther = false,
  onChange,
}: {
  value: string;
  options: Array<{ name: string }>;
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  emptyHighlight?: boolean;
  allowNone?: boolean;
  allowOther?: boolean;
  onChange: (value: string) => void;
}) {
  const [query, setQuery] = React.useState('');
  const q = query.trim().toLowerCase();
  const filtered = React.useMemo(() => {
    if (!q) return options;
    return options.filter((m) => m.name.toLowerCase().includes(q));
  }, [options, q]);

  const displayLabel =
    value === '__other__'
      ? 'Other'
      : value
        ? value
        : placeholder;

  return (
    <LightDropdown
      fullWidth
      matchTriggerWidth
      disabled={disabled}
      aria-label={ariaLabel}
      buttonClass={`gm-major-select-btn${emptyHighlight ? ' is-empty' : ''}`}
      menuClassName="gm-major-select-menu"
      label={
        <>
          <span className="gm-major-select-btn-label">{displayLabel}</span>
          <span className="gm-major-select-chevron" aria-hidden />
        </>
      }
    >
      {(close) => (
        <>
          <div className="gm-major-select-search">
            <input
              autoFocus
              type="search"
              value={query}
              placeholder="Search majors..."
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <div className="gm-major-select-list">
            {allowNone && (
              <button
                type="button"
                className={`gm-major-select-item${!value ? ' is-selected' : ''}`}
                onClick={() => {
                  onChange('');
                  setQuery('');
                  close();
                }}
              >
                <span className="gm-major-select-item-check">{!value ? '✓' : ''}</span>
                <span>None</span>
              </button>
            )}
            {filtered.map((m) => {
              const selected = value === m.name;
              return (
                <button
                  key={m.name}
                  type="button"
                  className={`gm-major-select-item${selected ? ' is-selected' : ''}`}
                  onClick={() => {
                    onChange(m.name);
                    setQuery('');
                    close();
                  }}
                >
                  <span className="gm-major-select-item-check">{selected ? '✓' : ''}</span>
                  <span>{m.name}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="gm-major-select-empty">No majors match “{query.trim()}”</div>
            )}
            {allowOther && (
              <button
                type="button"
                className={`gm-major-select-item${value === '__other__' ? ' is-selected' : ''}`}
                onClick={() => {
                  onChange('__other__');
                  setQuery('');
                  close();
                }}
              >
                <span className="gm-major-select-item-check">{value === '__other__' ? '✓' : ''}</span>
                <span>Other</span>
              </button>
            )}
          </div>
        </>
      )}
    </LightDropdown>
  );
}

// ── Saved Lists Tab (FEATURE-003 / UI-002) ─────────────────────────────────────
function isCounselorListGroup(list: CollegeListGroup): boolean {
  return list.created_by === 'counselor';
}

function SavedListsTab({
  studentId,
  studentLists,
  counselorLists,
  savedCollegeIds,
  onRefreshLists,
  onReloadMyColleges,
  onViewSearch,
  isStarter,
  maxLists,
  mySchoolsMax,
  onOpenInCam,
  onUpgrade,
}: {
  studentId: number;
  studentLists: CollegeListGroup[];
  counselorLists: CollegeListGroup[];
  savedCollegeIds: number[];
  onRefreshLists: () => Promise<void>;
  onReloadMyColleges: () => Promise<void>;
  onViewSearch: () => void;
  isStarter: boolean;
  maxLists: number;
  mySchoolsMax: number;
  onOpenInCam: (college: any) => void;
  onUpgrade: () => void;
}) {
  const [viewingList, setViewingList] = React.useState<CollegeListGroup | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [renameTarget, setRenameTarget] = React.useState<CollegeListGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<CollegeListGroup | null>(null);
  const { isPending, isKey, run } = usePendingAction();

  const studentListCount = studentLists.length;
  const canCreateList = studentListCount < maxLists;

  React.useEffect(() => {
    if (!viewingList?.id) return;
    const all = [...studentLists, ...counselorLists];
    const fresh = all.find((l) => l.id === viewingList.id);
    if (fresh) setViewingList(fresh);
  }, [studentLists, counselorLists, viewingList?.id]);

  const handleCreateList = async (name: string) => {
    const trimmed = name?.trim();
    if (!trimmed || isPending) return;
    const sid = Number(getUserId());
    if (!sid || Number.isNaN(sid)) {
      toast.error('Could not identify your account. Please sign in again.');
      return;
    }
    if (studentLists.some((l) => l.name === trimmed)) {
      toast.error('A list with this name already exists.');
      return;
    }
    await run('create-list', async () => {
      try {
        await collegeServices.createCollegeList(sid, trimmed);
        await onRefreshLists();
        setCreateOpen(false);
        toast.success(`Created list "${trimmed}"`);
      } catch {
        // http interceptor surfaces API errors
      }
    });
  };

  const handleRenameList = async (name: string) => {
    const list = renameTarget;
    if (!list?.id || isPending) return;
    const trimmed = name?.trim();
    if (!trimmed) return;
    if (trimmed === list.name) {
      setRenameTarget(null);
      return;
    }
    if (studentLists.some((l) => l.name === trimmed && l.id !== list.id)) {
      toast.error('A list with this name already exists.');
      return;
    }
    await run(`rename-${list.id}`, async () => {
      try {
        await collegeServices.updateCollegeList(list.id!, { name: trimmed });
        await onRefreshLists();
        const openList = viewingList;
        if (openList && openList.id === list.id) {
          setViewingList({
            ...openList,
            name: trimmed,
            collegeList: openList.collegeList,
          });
        }
        setRenameTarget(null);
        toast.success('List renamed');
      } catch {
        // http interceptor surfaces API errors
      }
    });
  };

  const handleDeleteList = () => {
    if (!deleteTarget?.id || isPending) return;
    void run('delete-list', async () => {
      await collegeServices.deleteCollegeList(deleteTarget.id!);
      await onRefreshLists();
      if (viewingList?.id === deleteTarget.id) setViewingList(null);
      toast.success(`Deleted list "${deleteTarget.name}"`);
      setDeleteTarget(null);
    });
  };

  const moveToMySchools = (college: any) => {
    if (isPending) return;
    const alreadyInMySchools = savedCollegeIds.includes(college.id);
    if (!alreadyInMySchools && savedCollegeIds.length >= mySchoolsMax) {
      toast.error(
        `You've reached the Starter limit of ${mySchoolsMax} schools in My Colleges. Upgrade for unlimited.`,
      );
      return;
    }
    void run(`move-${college.id}`, async () => {
      await collegeServices.moveCollegeToMySchools(studentId, college);
      await onReloadMyColleges();
      toast.success(`Added ${college.name} to My Schools`);
    });
  };

  const removeFromList = (college: any, list: CollegeListGroup) => {
    if (!list.id || isPending) return;
    void run(`remove-${college.id}`, async () => {
      await collegeServices.removeCollegeFromList(list.id!, college.id, studentId);
      await onRefreshLists();
      const openList = viewingList;
      if (openList && openList.id === list.id) {
        setViewingList({
          ...openList,
          name: openList.name,
          collegeList: openList.collegeList.filter((c) => c.id !== college.id),
        });
      }
      toast.success(`Removed ${college.name} from "${list.name}"`);
    });
  };

  const handleOpenCam = (college: any) => {
    if (isPending) return;
    const admissionKey = normalizeAdmissionResultUi(college.admission_result);
    if (admissionKey === 'not-applying') {
      toast.error('This school is marked Not applying. Update admission status to open in CAM.');
      return;
    }
    if (isStarter) {
      onUpgrade();
      return;
    }
    onOpenInCam(college);
  };

  return (
    <>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="gm-btn-navy"
          style={{ borderRadius: 6, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          disabled={!canCreateList || isPending}
          onClick={() => {
            if (!canCreateList) {
              toast.error(
                isStarter
                  ? 'Upgrade to add more lists.'
                  : 'Maximum number of lists reached.',
              );
              return;
            }
            setCreateOpen(true);
          }}
        >
          <IconPlus /> Create New List
        </button>
      </div>

      <ModalAddList
        open={createOpen}
        submitting={isKey('create-list')}
        onCancel={() => !isPending && setCreateOpen(false)}
        handleClose={(name) => void handleCreateList(name)}
        title="Create New List"
        submitLabel="Create List"
        submittingLabel="Creating..."
      />

      <ModalAddList
        open={!!renameTarget}
        initialName={renameTarget?.name ?? ''}
        submitting={!!renameTarget?.id && isKey(`rename-${renameTarget.id}`)}
        onCancel={() => !isPending && setRenameTarget(null)}
        handleClose={(name) => void handleRenameList(name)}
        title="Rename list"
        submitLabel="Save"
        submittingLabel="Saving..."
      />

      <section className="gm-saved-lists-section">
        <h3 className="gm-saved-lists-section-title">Your Lists</h3>
        {studentLists.length === 0 ? (
          <div className="gm-saved-lists-empty">
            <p style={{ marginBottom: 16 }}>
              Create your first list to organize schools you&apos;re considering.
            </p>
            <button
              type="button"
              className="gm-btn-orange"
              style={{ borderRadius: 8, fontSize: 14 }}
              onClick={onViewSearch}
            >
              Go to College Search
            </button>
          </div>
        ) : (
          <div className="gm-saved-lists-grid">
            {studentLists.map((list) => (
              <SavedListCard
                key={list.id ?? `${list.name}-${list.created_by ?? 'student'}`}
                list={list}
                editable
                actionsDisabled={isPending}
                renameLoading={isKey(`rename-${list.id}`)}
                onView={() => !isPending && setViewingList(list)}
                onRename={() => !isPending && setRenameTarget(list)}
                onDelete={() => !isPending && setDeleteTarget(list)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="gm-saved-lists-section">
        <h3 className="gm-saved-lists-section-title">Lists from Your Counselor</h3>
        {counselorLists.length === 0 ? (
          <div className="gm-saved-lists-empty">
            <p>Your counselor hasn&apos;t created any lists for you yet.</p>
          </div>
        ) : (
          <div className="gm-saved-lists-grid">
            {counselorLists.map((list) => (
              <SavedListCard
                key={list.id ?? `counselor-${list.name}`}
                list={list}
                fromCounselor={list.counselor_name || 'Your counselor'}
                onView={() => setViewingList(list)}
              />
            ))}
          </div>
        )}
      </section>

      {viewingList &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9990,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
          >
            <div
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }}
              onClick={() => !isPending && setViewingList(null)}
              aria-hidden
            />
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                background: 'white',
                borderRadius: 16,
                width: '100%',
                maxWidth: 720,
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              }}
            >
              <div
                style={{
                  padding: '20px 24px 16px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                }}
              >
                <div>
                  <h3
                    className="gm-display"
                    style={{ fontWeight: 700, fontSize: 18, color: BRAND_NAVY, margin: 0 }}
                  >
                    {viewingList.name}
                  </h3>
                  <p
                    style={{
                      fontSize: 12,
                      color: '#6B7280',
                      margin: '2px 0 0',
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {viewingList.collegeList.length} school
                    {viewingList.collegeList.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => !isPending && setViewingList(null)}
                  disabled={isPending}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: isPending ? 'not-allowed' : 'pointer',
                    padding: 6,
                    borderRadius: 8,
                    color: '#6B7280',
                    display: 'flex',
                    opacity: isPending ? 0.5 : 1,
                  }}
                  aria-label="Close"
                >
                  <IconX size={18} />
                </button>
              </div>

              <div style={{ overflowY: 'auto', padding: '16px 24px 32px', flex: 1 }}>
                {isCounselorListGroup(viewingList) && (
                  <div className="gm-list-detail-banner">
                    From <strong>{viewingList.counselor_name || 'your counselor'}</strong> — this
                    list is read-only. Use Move to My Schools to start applying.
                  </div>
                )}

                {viewingList.collegeList.length === 0 ? (
                  <div className="gm-saved-lists-empty">
                    <p style={{ marginBottom: 16 }}>No schools in this list yet.</p>
                    {!isCounselorListGroup(viewingList) && (
                      <button
                        type="button"
                        className="gm-btn-orange"
                        style={{ borderRadius: 8, fontSize: 14 }}
                        onClick={onViewSearch}
                      >
                        Browse College Search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="gm-list-detail-stack">
                    {viewingList.collegeList.map((college) => (
                      <ListDetailCollegeRow
                        key={college.id}
                        college={college}
                        readOnly={isCounselorListGroup(viewingList)}
                        inMySchools={savedCollegeIds.includes(college.id)}
                        actionsDisabled={isPending}
                        moveLoading={isKey(`move-${college.id}`)}
                        removeLoading={isKey(`remove-${college.id}`)}
                        onOpenInCam={() => handleOpenCam(college)}
                        onMoveToMySchools={() => moveToMySchools(college)}
                        onRemoveFromList={() => removeFromList(college, viewingList)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      <Modal
        show={!!deleteTarget}
        onHide={() => !isPending && setDeleteTarget(null)}
        centered
        backdrop={isPending ? 'static' : true}
        keyboard={!isPending}
      >
        <Modal.Header closeButton={!isPending}>
          <Modal.Title>Delete &quot;{deleteTarget?.name}&quot;?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
          Schools in this list will stay in My Schools or other lists where you saved them.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="gm-btn-navy-outline"
            style={{ borderRadius: 8, marginRight: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            disabled={isPending}
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="gm-btn-orange"
            style={{ borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            disabled={isPending}
            onClick={handleDeleteList}
          >
            {isKey('delete-list') ? (
              <>
                <InlineSpinner tone="light" /> Deleting...
              </>
            ) : (
              'Delete list'
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function SavedListCard({
  list,
  editable,
  fromCounselor,
  actionsDisabled,
  renameLoading,
  onView,
  onRename,
  onDelete,
}: {
  list: CollegeListGroup;
  editable?: boolean;
  fromCounselor?: string;
  actionsDisabled?: boolean;
  renameLoading?: boolean;
  onView: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}) {
  const logos = list.collegeList.slice(0, 4).map((c) => getInitials(c.name || '?'));

  return (
    <div className={`gm-list-card${actionsDisabled ? ' is-actions-disabled' : ''}`}>
      {fromCounselor && (
        <div className="gm-counselor-list-badge">
          <span className="gm-counselor-list-badge-avatar">
            {fromCounselor
              .split(' ')
              .slice(0, 2)
              .map((w) => w[0])
              .join('')}
          </span>
          from {fromCounselor}
        </div>
      )}
      <h4
        className="gm-display"
        style={{ fontWeight: 600, fontSize: 15, color: '#111827', marginBottom: 2 }}
      >
        {list.name}
      </h4>
      <p style={{ fontSize: 12, color: '#6B7280', fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
        {list.collegeList.length} school{list.collegeList.length !== 1 ? 's' : ''}
      </p>

      {logos.length > 0 && (
        <div className="gm-list-card-logos">
          {logos.map((l, li) => (
            <div
              key={li}
              className="gm-list-card-logo"
              style={{ marginLeft: li === 0 ? 0 : -8 }}
            >
              {l}
            </div>
          ))}
        </div>
      )}

      <div className="gm-list-card-footer">
        <button
          type="button"
          className="gm-list-view-btn"
          onClick={onView}
          disabled={actionsDisabled}
        >
          <IconEye /> View
        </button>
        {editable && (
          <div className="gm-list-card-actions">
            <button
              type="button"
              className="gm-list-icon-btn"
              aria-label="Rename list"
              onClick={onRename}
              disabled={actionsDisabled}
            >
              {renameLoading ? <InlineSpinner tone="dark" /> : <IconPencil />}
            </button>
            <button
              type="button"
              className="gm-list-icon-btn danger"
              aria-label="Delete list"
              onClick={onDelete}
              disabled={actionsDisabled}
            >
              <IconTrash />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ListDetailCollegeRow({
  college: c,
  readOnly,
  inMySchools,
  actionsDisabled,
  moveLoading,
  removeLoading,
  onOpenInCam,
  onMoveToMySchools,
  onRemoveFromList,
}: {
  college: any;
  readOnly: boolean;
  inMySchools: boolean;
  actionsDisabled?: boolean;
  moveLoading?: boolean;
  removeLoading?: boolean;
  onOpenInCam: () => void;
  onMoveToMySchools: () => void;
  onRemoveFromList: () => void;
}) {
  const rowBusy = moveLoading || removeLoading;
  const initials = getInitials(c.name || '');
  const typeLabel = type_of_school_convert(c.type_of_school);
  const isNotApplying =
    normalizeAdmissionResultUi(c.admission_result) === 'not-applying';
  const location = [c.city, c.state].filter(Boolean).join(', ');

  return (
    <article
      className={`gm-list-detail-card${rowBusy ? ' is-row-busy' : ''}`}
    >
      {!readOnly && (
        <button
          type="button"
          className="gm-list-detail-card-remove"
          onClick={onRemoveFromList}
          disabled={actionsDisabled}
          aria-label={`Remove ${c.name} from list`}
          title="Remove from list"
        >
          {removeLoading ? <InlineSpinner tone="dark" /> : <IconX size={16} />}
        </button>
      )}

      <div className="gm-list-detail-card-top">
        <div className="gm-college-avatar gm-list-detail-avatar">{initials}</div>
        <div className="gm-list-detail-card-info">
          <h4 className="gm-list-detail-card-name">{c.name}</h4>
          {location ? <p className="gm-list-detail-card-location">{location}</p> : null}
          <div className="gm-list-detail-card-chips">
            {c.year_type ? <span className="gm-type-chip">{c.year_type}</span> : null}
            {typeLabel ? <span className="gm-type-chip">{typeLabel}</span> : null}
            {inMySchools ? (
              <span className="gm-type-chip gm-chip-in-my-schools">In My Schools</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="gm-list-detail-card-actions">
        <button
          type="button"
          className="gm-btn-cam-sm"
          onClick={onOpenInCam}
          disabled={actionsDisabled || isNotApplying}
          title={isNotApplying ? 'Not applying' : 'Open in CAM'}
        >
          Open in CAM
        </button>
        <button
          type="button"
          className={`gm-btn-sage${inMySchools ? ' is-added' : ''}`}
          onClick={onMoveToMySchools}
          disabled={actionsDisabled || inMySchools || moveLoading}
          title={inMySchools ? 'Already in My Schools' : 'Move to My Schools'}
        >
          {moveLoading ? (
            <>
              <InlineSpinner tone="light" /> Moving...
            </>
          ) : inMySchools ? (
            <>
              <IconCheck /> In My Schools
            </>
          ) : (
            'Move to My Schools'
          )}
        </button>
      </div>
    </article>
  );
}

export default MyCollege;
